import { useState, useEffect } from 'react'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { mapCodesToNames } from '../../../../utilities/MapCodesToNames'
import { toCamelCase } from '../../../../utilities/formatters/camelCase'

const TimeRangeComplex = ({ ruleParameters, setRuleParameters, desc, isEditing, filterTemplate }) => {
  const [tzOffset, setTzOffset] = useState(null)

  const [days, setDays] = useState([])

  const [hours, setHours] = useState({})
  const [hoursChange, setHoursChange] = useState(false)
  const [min, setMin] = useState('00:00')
  const [minChange, setMinChange] = useState(false)
  const [max, setMax] = useState('23:59')
  const [maxChange, setMaxChange] = useState(false)

  const [change, setChange] = useState(false)
  const [filter, setFilter] = useState({})

  const { t } = useTranslation(['common', 'rules'])

  const getIndex = () => {
    if (Array.isArray(ruleParameters) && ruleParameters?.length === 0) return -1
    if (Array.isArray(ruleParameters)) return ruleParameters?.map((e) => e.filterTemplate).indexOf(filterTemplate)
    return -1
  }

  useEffect(() => {
    const aux = ruleParameters?.find((e) => e.filterTemplate === filterTemplate)

    //PUPULAR ESTADOS INICIALES
    if (aux?.targetRange) {
      setFilter({ targetRange: aux.targetRange })
      if (aux.targetRange.tzOffset) setTzOffset(aux.targetRange.tzOffset)
      if (aux.targetRange.days) setDays(aux.targetRange.days)
      if (aux.targetRange.hours) {
        setHours(aux.targetRange.hours)
        if (aux.targetRange.hours.min) setMin(aux.targetRange.hours.min)
        if (aux.targetRange.hours.max) setMax(aux.targetRange.hours.max)
      }
    }
  }, [ruleParameters, filterTemplate])

  const handleTimeOffsetChange = (event, value) => {
    setTzOffset(value)
    setFilter({ targetRange: { ...(filter.targetRange || {}), tzOffset: value } })
    setChange(true)
  }

  const handleDaysChange = (event, value) => {
    setDays(value)
    setFilter({ targetRange: { ...(filter.targetRange || {}), days: value } })
    setChange(true)
  }

  useEffect(() => {
    if (minChange) {
      setHours({ ...hours, min })
      setMinChange(false)
      setHoursChange(true)
    }
  }, [minChange])

  useEffect(() => {
    if (maxChange) {
      setHours({ ...hours, max })
      setMaxChange(false)
      setHoursChange(true)
    }
  }, [maxChange])

  useEffect(() => {
    if (hoursChange) {
      setFilter({ targetRange: { ...(filter.targetRange || {}), hours } })
      setHoursChange(false)
      setChange(true)
    }
  }, [hoursChange])

  useEffect(() => {
    const index = getIndex()

    if (change) {
      let all = [...ruleParameters]
      let previous = index >= 0 && all[index] ? { ...all[index] } : {}

      previous.targetRange = filter.targetRange
      previous.filterTemplate = filterTemplate

      if (index !== -1) {
        all.splice(index, 1)
      }
      all.push(previous)

      setRuleParameters([...all])
    }
    setChange(false)
  }, [change])

  return (
    <Box className="select-single-container">
      <Typography variant="subtitle2">{`${t('common:hours')} (${t('common:format')}: HH:MM)`}</Typography>
      <TextField
        size="small"
        type="text"
        disabled={!isEditing}
        variant="outlined"
        value={min}
        onChange={(event) => {
          setMin(event.target.value)
          setMinChange(true)
        }}
      />
      <TextField
        size="small"
        type="text"
        disabled={!isEditing}
        variant="outlined"
        value={max}
        onChange={(event) => {
          setMax(event.target.value)
          setMaxChange(true)
        }}
      />

      <Typography variant="subtitle2">{t('common:days')}</Typography>
      <Autocomplete
        multiple
        options={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']}
        getOptionLabel={(option) => mapCodesToNames(option)}
        filterSelectedOptions
        value={days}
        disabled={!isEditing}
        onChange={handleDaysChange}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={`${t('common:select')} ${t('common:or')} ${t('common:search')}...`}
          />
        )}
      />

      <Typography variant="subtitle2">{t('common:timeZoneOffset')}</Typography>
      <Autocomplete
        options={[
          '-12',
          '-11',
          '-10',
          '-9',
          '-8',
          '-7',
          '-6',
          '-5',
          '-4',
          '-3',
          '-2',
          '-1',
          '0',
          '+1',
          '+2',
          '+3',
          '+4',
          '+5',
          '+6',
          '+7',
          '+8',
          '+9',
          '+10',
          '+11',
          '+12',
        ]}
        getOptionLabel={(option) => mapCodesToNames(option)}
        filterSelectedOptions
        value={tzOffset}
        disabled={!isEditing}
        onChange={handleTimeOffsetChange}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={`${t('common:select')} ${t('common:or')} ${t('common:search')}...`}
          />
        )}
      />
    </Box>
  )
}

export default TimeRangeComplex
